import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import {
    Inbox, ImagePlus, ExternalLink, Phone, MessageCircle, Mail, CheckCircle2,
    ArrowRight, Star, ShieldCheck, MapPin,
} from "lucide-react";
import { api, fileUrl, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import MyPageShell, { EmptyState } from "../components/MyPageShell";
import { SERVICE_ROLES, isServiceRole, isListingRole, isAdmin } from "../lib/roles";
import ReferEarnCard from "../components/ReferEarnCard";

const SERVICE_LABELS = {
    loan_agent: "Loan Agent / Home Loan",
    advocate: "Advocate / Property Lawyer",
    architect: "Architect",
    interior_designer: "Interior Designer",
    civil_engineer: "Civil Engineer",
    civil_contractor: "Civil Contractor",
    property_valuer: "Property Valuer / Surveyor",
    renovation: "Renovation / Construction Services",
};

// Role-specific dashboard section titles (per user's spec)
const ROLE_LABELS = {
    loan_agent: {
        profileTitle: "My Loan Agent Profile",
        portfolioTitle: "Loan Products / Service Photos",
        emptyPortfolio: "Add photos of loan documents, sanctioned deals, office & branding.",
    },
    advocate: {
        profileTitle: "My Legal Service Profile",
        portfolioTitle: "Legal Services / Portfolio",
        emptyPortfolio: "Upload photos of case work, court documents (redacted), office & credentials.",
    },
    architect: {
        profileTitle: "My Architect Profile",
        portfolioTitle: "Architectural Portfolio",
        emptyPortfolio: "Add house plans, elevations, completed project shots.",
    },
    interior_designer: {
        profileTitle: "My Interior Designer Profile",
        portfolioTitle: "Interior Portfolio",
        emptyPortfolio: "Add photos of bedrooms, kitchens, offices, ceilings and finished spaces.",
    },
    civil_engineer: {
        profileTitle: "My Civil Engineer Profile",
        portfolioTitle: "Engineering Work Portfolio",
        emptyPortfolio: "Upload structural drawings, site inspections and completed projects.",
    },
    civil_contractor: {
        profileTitle: "My Contractor Profile",
        portfolioTitle: "Construction Work Portfolio",
        emptyPortfolio: "Add site progress, completed buildings and material quality photos.",
    },
    property_valuer: {
        profileTitle: "My Valuer / Surveyor Profile",
        portfolioTitle: "Valuation & Survey Portfolio",
        emptyPortfolio: "Add survey site photos, sample valuation reports and instruments.",
    },
    renovation: {
        profileTitle: "My Renovation Services Profile",
        portfolioTitle: "Before / After Renovation Portfolio",
        emptyPortfolio: "Add before-and-after photos of homes and offices you have renovated.",
    },
};

/**
 * MyServicesDashboard — unified personal dashboard for every service professional.
 * Shows: incoming enquiries (with Call/WhatsApp/mark-responded), portfolio strip,
 * public profile preview link.
 */
export default function MyServicesDashboard() {
    const { user } = useAuth();
    const [tab, setTab] = useState("enquiries");
    const [enquiries, setEnquiries] = useState(null);
    const [portfolio, setPortfolio] = useState(null);
    const [busy, setBusy] = useState(null);

    const userIsServiceRole = user && isServiceRole(user.role);

    useEffect(() => {
        if (!userIsServiceRole) return;
        api.get("/inquiries/mine").then((r) => {
            const received = (r.data || []).filter((i) => i.professional_id === user.id || i.property_owner_id === user.id);
            setEnquiries(received);
        }).catch(() => setEnquiries([]));
        api.get("/me/portfolio").then((r) => setPortfolio(r.data || [])).catch(() => setPortfolio([]));
    }, [userIsServiceRole, user]);

    if (!user) return null;
    // Strict isolation — property-listing roles (broker/seller/builder) belong on /dashboard, not here.
    if (isListingRole(user.role) || isAdmin(user.role)) {
        return <Navigate to="/dashboard" replace />;
    }
    if (!userIsServiceRole) {
        // Buyers / legacy `user` role → back to their dashboard.
        return <Navigate to="/dashboard" replace />;
    }

    const markResponded = async (id) => {
        setBusy(id);
        try {
            await api.patch(`/inquiries/${id}`, { status: "responded" });
            setEnquiries((prev) => prev.map((e) => e.id === id ? { ...e, status: "responded" } : e));
            // 1-tap review request: generate a single-use magic link and open WhatsApp
            // pre-filled with the message. Failure is silent (still counts as responded).
            try {
                const { data } = await api.post("/reviews/request-token", { inquiry_id: id });
                const reviewUrl = data?.review_path ? `${window.location.origin}${data.review_path}` : data?.review_url;
                const phoneDigits = (data?.reviewer_phone || "").replace(/[^0-9]/g, "");
                const waText = `Hi ${data?.reviewer_name || ""}, thanks for reaching out to me on Global Property Mart. If our conversation was helpful, would you please share a quick honest review? It takes less than a minute: ${reviewUrl}`;
                const waUrl = phoneDigits && reviewUrl ? `https://wa.me/${phoneDigits}?text=${encodeURIComponent(waText)}` : null;
                if (waUrl) {
                    window.open(waUrl, "_blank", "noopener,noreferrer");
                    toast.success("Marked as responded — WhatsApp opened for review request");
                } else if (reviewUrl) {
                    try { await navigator.clipboard.writeText(reviewUrl); } catch { /* ignore */ }
                    toast.success("Marked responded — review link copied (no phone on file)");
                } else {
                    toast.success("Marked as responded");
                }
            } catch {
                toast.success("Marked as responded");
            }
        } catch {
            toast.error("Failed to update");
        } finally { setBusy(null); }
    };

    const requestReviewAgain = async (id, phone) => {
        try {
            const { data } = await api.post("/reviews/request-token", { inquiry_id: id });
            const reviewUrl = data?.review_path ? `${window.location.origin}${data.review_path}` : data?.review_url;
            const phoneDigits = (data?.reviewer_phone || phone || "").replace(/[^0-9]/g, "");
            const waText = `Hi ${data?.reviewer_name || ""}, could you please share a quick honest review of our conversation on Global Property Mart? It takes less than a minute: ${reviewUrl}`;
            const waUrl = phoneDigits && reviewUrl ? `https://wa.me/${phoneDigits}?text=${encodeURIComponent(waText)}` : null;
            if (waUrl) {
                window.open(waUrl, "_blank", "noopener,noreferrer");
                toast.success("WhatsApp opened with review link");
            } else if (reviewUrl) {
                try { await navigator.clipboard.writeText(reviewUrl); toast.success("Review link copied to clipboard"); } catch { toast.error("No phone / clipboard access"); }
            }
        } catch (err) {
            const msg = err?.response?.data?.detail;
            if (typeof msg === "string" && msg.includes("already been submitted")) toast.info("Review already submitted for this enquiry");
            else toast.error("Could not create review link");
        }
    };

    const openCount = enquiries?.filter((e) => e.status !== "responded" && e.status !== "closed").length || 0;
    const roleLabels = ROLE_LABELS[user.role] || { profileTitle: "My Service Profile", portfolioTitle: "Portfolio", emptyPortfolio: "Upload your best work." };

    return (
        <MyPageShell
            title={roleLabels.profileTitle}
            subtitle={SERVICE_LABELS[user.role] || "Manage enquiries, portfolio & public profile"}
            testid="my-services-page"
        >
            {/* Quick stats */}
            <div className="grid grid-cols-3 gap-2 mb-4">
                <StatCard icon={Inbox} label="Open enquiries" value={openCount} accent />
                <StatCard icon={ImagePlus} label="Portfolio photos" value={portfolio?.length ?? "—"} />
                <StatCard icon={user.verified ? ShieldCheck : Star} label={user.verified ? "Verified" : "Not verified"} value={user.verified ? "✓" : "—"} />
            </div>

            <div className="mb-4">
                <ReferEarnCard theme="light" />
            </div>

            {/* Public profile link */}
            <Link
                to={`/services/professional/${user.id}`}
                target="_blank"
                data-testid="ms-public-preview"
                className="flex items-center justify-between gap-2 rounded-xl border border-navy-500/10 bg-cream-50 hover:bg-gold-500/5 px-3 py-2.5 mb-4"
            >
                <div className="flex items-center gap-2 text-sm text-navy-500 font-semibold">
                    <ExternalLink className="w-4 h-4 text-gold-600" /> Preview my public profile
                </div>
                <ArrowRight className="w-4 h-4 text-navy-500/40" />
            </Link>

            {/* Tabs */}
            <div className="flex gap-2 mb-3">
                {[
                    { v: "enquiries", l: `Enquiries${openCount ? ` (${openCount})` : ""}` },
                    { v: "portfolio", l: `${roleLabels.portfolioTitle}${portfolio?.length ? ` (${portfolio.length})` : ""}` },
                ].map((t) => (
                    <button
                        key={t.v}
                        onClick={() => setTab(t.v)}
                        data-testid={`ms-tab-${t.v}`}
                        className={`px-3 py-1.5 rounded-full text-xs font-bold ${tab === t.v ? "bg-gold-500 text-navy-500" : "bg-white text-navy-500/70 border border-navy-500/15 hover:border-gold-500/40"}`}
                    >
                        {t.l}
                    </button>
                ))}
            </div>

            {tab === "enquiries" && (
                <EnquiriesList items={enquiries} onMarkResponded={markResponded} onRequestReview={requestReviewAgain} busy={busy} publicProfileUrl={`/services/professional/${user.id}`} />
            )}

            {tab === "portfolio" && (
                <PortfolioStrip items={portfolio} emptyMessage={roleLabels.emptyPortfolio} />
            )}
        </MyPageShell>
    );
}

function StatCard({ icon: Icon, label, value, accent }) {
    return (
        <div className={`rounded-xl border ${accent ? "border-gold-500/40 bg-gold-500/5" : "border-navy-500/10 bg-cream-50"} p-3 text-center`}>
            <Icon className={`w-5 h-5 mx-auto mb-1 ${accent ? "text-gold-700" : "text-navy-500/60"}`} />
            <div className="text-lg font-heading font-black text-navy-500 leading-none">{value}</div>
            <div className="text-[10px] uppercase tracking-widest text-navy-500/50 font-bold mt-1">{label}</div>
        </div>
    );
}

function EnquiriesList({ items, onMarkResponded, onRequestReview, busy, publicProfileUrl }) {
    if (items === null) return <div className="py-10 text-center text-sm text-navy-500/50">Loading…</div>;
    if (items.length === 0) {
        return (
            <EmptyState
                icon={Inbox}
                title="No enquiries yet"
                message="When buyers reach out via your public profile, their enquiries will appear here with quick Call & WhatsApp actions."
                cta="View my public profile"
                to={publicProfileUrl}
                testid="ms-empty-enquiries"
            />
        );
    }
    return (
        <ul className="space-y-3">
            {items.map((e) => {
                const phoneDigits = (e.phone || "").replace(/[^0-9]/g, "");
                const callHref = phoneDigits ? `tel:${phoneDigits}` : null;
                const waHref = phoneDigits ? `https://wa.me/${phoneDigits}?text=${encodeURIComponent(`Hi ${e.name}, thanks for your enquiry on Global Property Mart. `)}` : null;
                const mailHref = e.email ? `mailto:${e.email}?subject=${encodeURIComponent("Your enquiry on Global Property Mart")}` : null;
                const isOpen = e.status !== "responded" && e.status !== "closed";
                return (
                    <li key={e.id} data-testid={`ms-enq-${e.id}`} className="rounded-xl border border-navy-500/10 bg-white p-3">
                        <div className="flex items-start justify-between gap-2 flex-wrap">
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <div className="font-heading font-bold text-navy-500 text-sm truncate">{e.name}</div>
                                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full font-bold ${isOpen ? "bg-amber-100 text-amber-800 border border-amber-300" : "bg-emerald-100 text-emerald-800 border border-emerald-300"}`}>
                                        {isOpen ? "new" : e.status}
                                    </span>
                                </div>
                                <div className="text-[11px] text-navy-500/55 mt-0.5">
                                    {e.type === "service" ? "Service enquiry" : e.property_title || "Property enquiry"} · {new Date(e.created_at).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                                </div>
                                {e.message && <p className="mt-2 text-[13px] text-navy-500/80 leading-relaxed">{e.message}</p>}
                            </div>
                        </div>
                        <div className="mt-3 flex gap-2 flex-wrap">
                            {callHref && (
                                <a href={callHref} data-testid={`ms-enq-call-${e.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-emerald-600 text-white text-xs font-extrabold hover:bg-emerald-500">
                                    <Phone className="w-3 h-3" /> {e.phone}
                                </a>
                            )}
                            {waHref && (
                                <a href={waHref} target="_blank" rel="noreferrer" data-testid={`ms-enq-wa-${e.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-[#25D366] text-white text-xs font-extrabold hover:opacity-90">
                                    <MessageCircle className="w-3 h-3" /> WhatsApp
                                </a>
                            )}
                            {mailHref && (
                                <a href={mailHref} data-testid={`ms-enq-mail-${e.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-gold-500 text-navy-500 text-xs font-extrabold hover:bg-gold-400">
                                    <Mail className="w-3 h-3" /> Email
                                </a>
                            )}
                            {isOpen && (
                                <button
                                    onClick={() => onMarkResponded(e.id)}
                                    disabled={busy === e.id}
                                    data-testid={`ms-enq-done-${e.id}`}
                                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-emerald-600 text-white text-xs font-extrabold hover:bg-emerald-500 disabled:opacity-50"
                                    title="Mark responded and send review request via WhatsApp"
                                >
                                    <CheckCircle2 className="w-3 h-3" /> Mark responded & ask for review
                                </button>
                            )}
                            {!isOpen && (
                                <button
                                    onClick={() => onRequestReview(e.id, e.phone)}
                                    data-testid={`ms-enq-review-${e.id}`}
                                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-gold-500/60 text-gold-700 text-xs font-extrabold hover:bg-gold-500/10"
                                    title="Send another review request"
                                >
                                    <Star className="w-3 h-3" /> Send review link
                                </button>
                            )}
                        </div>
                    </li>
                );
            })}
        </ul>
    );
}

function PortfolioStrip({ items, emptyMessage }) {
    if (items === null) return <div className="py-10 text-center text-sm text-navy-500/50">Loading…</div>;
    return (
        <div>
            <div className="flex items-center justify-between mb-3">
                <div className="text-[11px] uppercase tracking-widest text-navy-500/50 font-bold">
                    {items.length ? `${items.length} photo${items.length !== 1 ? "s" : ""}` : "No photos yet"}
                </div>
                <Link to="/my-portfolio" data-testid="ms-portfolio-manage" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-gold-500 text-navy-500 text-xs font-extrabold hover:bg-gold-400">
                    <ImagePlus className="w-3.5 h-3.5" /> {items.length ? "Manage portfolio" : "Add your first photo"}
                </Link>
            </div>
            {items.length === 0 ? (
                <div className="py-8 text-center text-sm text-navy-500/60 rounded-xl bg-navy-500/5 border border-navy-500/10 px-4">
                    {emptyMessage || "Upload your best work — appears on the home carousel and your public profile."}
                </div>
            ) : (
                <ul className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {items.slice(0, 6).map((p) => (
                        <li key={p.id} className="rounded-lg overflow-hidden bg-navy-500/5 aspect-square">
                            <img src={fileUrl(p.image_url)} alt={p.title || ""} className="w-full h-full object-cover" loading="lazy" />
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
